import React from "react";
import { connect } from "~/redux/connect";
import { settingsActionCreator } from "~/actions/settingsActions";
import { getFitleredViewState } from "~/StateGetters/FilteredViewStateGetters";
import { Box, Button, HStack, Input, Pressable, Tooltip, View, useThemeColors } from "~/native-ui";


const initialState = { isEditting: false, isHover: false, showButtons: false };

const reducer = (state, action) => {
    switch (action.type) {
        case 'hover':
            return {...state, isHover: action.isHover, showButtons: state.isEditting || action.isHover, };
        case 'edit':
            const isEditting = action.isEditting ?? !state.isEditting;
            return {...state, isEditting, showButtons: isEditting || state.isHover };
      }
}

const mapStateToProps = (state, { modelId, view }) => {
    const filterState = getFitleredViewState(state, modelId),
          filteredView = filterState.getView(view);
    return {
        filteredView,
        originalTitle: filteredView.title ? null : filterState.getWorkingView().previousTitle,
        isOwnView: !filterState.isSharedView(view),
        isActive: view === filterState.activeFilteredViewName,
    }
}

const mapDispatchToProps = (dispatch, { modelId, view }) => {
    const settingActions = settingsActionCreator(modelId);
    return {
        deleteView: () => dispatch(settingActions.deleteView({view})),
        renameView: (props) => dispatch(settingActions.renameView({view, ...props })),
        setActiveView: (view) => dispatch(settingActions.setActiveView({view})),
        shareView: () => dispatch(settingActions.shareView({view})),
    }
}

const FilteredViewRow = (props) => {
    const inputRef = React.useRef(null),
          editButtonRef = React.useRef(null),
        { filteredView, isOwnView, isActive, originalTitle, } = props,
        { deleteView, setActiveView, renameView, shareView, onGoToView, trans } = props,
        { title, isShared } = filteredView,
        [{ isEditting, isHover, showButtons }, dispatch] = React.useReducer(reducer, initialState),
        { primaryHover } = useThemeColors(),
        editIcon = isEditting ? "save" : "edit",
        editTooltip = isEditting ? trans("views.saveViewName") : trans("views.renameView"),
        shareTooltip = !isOwnView ? trans("views.sharedView") : isShared ? trans("views.unshareView") : trans("views.shareView"),
        deleteTooltip = trans("views.deleteView"),



        filteredViewRef = React.useRef(filteredView),
        iconProps = React.useRef({ fontSize: "xs", primary: true, iconProps: { size: "xs" } }).current,
        buttonGroupProps = React.useMemo(() => ({ style: showButtons ? {} : { visibility: "hidden" } }) , [showButtons]),
        unmountedRef = React.useRef(false),



        /** 
         * @param e Synthetic Event
         */
        onDelete = React.useCallback((e) => {
            e.preventDefault();
            e.stopPropagation();
            deleteView();
        }, [ deleteView ]),


        
        /** 
         * @param e Synthetic Event
         */
        handleChangeActiveView = React.useCallback((e) => {
            e.preventDefault();
            e.stopPropagation();

            setActiveView(filteredViewRef.current);
            onGoToView();
        }, [ filteredViewRef, isEditting, setActiveView, onGoToView ]),


        
        
        /** 
         * @param e: Synthetic Event
         * @param isEditting: boolean | undefined
         */
        setEditting = React.useCallback((e, isEditting) => {
            e.preventDefault();
            dispatch({ type: "edit", isEditting })
        }, [ dispatch, editButtonRef ]),


        
        
        /** 
         * @param e: Synthetic Event
         */
        onBlur = React.useCallback((e) => {
            if (unmountedRef.current) return;
            if (e.relatedTarget === editButtonRef.current) { return; }
            !filteredViewRef.current?.title && renameView({title: originalTitle, previousTitle: "" });
            e.persist();
            setTimeout(() => {  setEditting(e, false) }, 300);
        }, [isEditting, originalTitle]),


        
        
        /** 
         * @param e: Synthetic Event
         */
        onMouseOver = React.useCallback((e) => {
            dispatch({ type: "hover", isHover: true })
        }, [ ]),


        
        
        /** 
         * @param e: Synthetic Event
         */
        onMouseLeave = React.useCallback((e) => { 
            if (unmountedRef.current) return;
            dispatch({ type: "hover", isHover: false, })
        }, [ ]),


        
        
        /** 
         * @param e: Synthetic Event
         */
        onRename = React.useCallback((e) => {
            if (unmountedRef.current) return;
            renameView({ title: e })
        }, [ ]),


             
        hoveredState = React.useMemo(() => ({ backgroundColor: "transparent" }), [])
    ;


    React.useEffect(() => {
        filteredViewRef.current = filteredView
    })


    /**
     * On editting, set focus.
     */
    React.useEffect(() => {
        if (isEditting) inputRef.current?.focus();
    }, [ isEditting, inputRef ]);





    /**
     * On unmount set unmountedRef
     */
     React.useEffect(() => () => { unmountedRef.current = true }, []);


    return (
        <View p="1" onMouseOver={onMouseOver} onMouseLeave={onMouseLeave}>
            <Pressable position="absolute" top="0" bottom="0" left="0" right="0" borderRadius="4" bg={(isActive || (isHover && !isEditting)) ? primaryHover : undefined} onPress={isEditting ? null : handleChangeActiveView} />
            <HStack space="2" px="2" alignItems="center">
                <Box flex="1">
                    <Input ref={inputRef} value={title} placeholder="Enter Text" onChange={onRename} onBlur={onBlur} isReadOnly={!isEditting} />
                    <React.Fragment>
                        {
                            isEditting
                                ? null
                                : <Pressable onPress={handleChangeActiveView} position="absolute" top="0" bottom="0" left="-5px" right="-5px" borderRadius="4"/>
                        }
                    </React.Fragment>
                </Box>
                {
                    !isOwnView
                        ? null
                        : (
                            <Button.ButtonGroup flexShrink="0" space="1" alignItems="center" >
                                <Tooltip tooltip={editTooltip}><Button icon={editIcon} ref={editButtonRef} onPress={setEditting} { ...iconProps } { ...buttonGroupProps } /></Tooltip>
                                <Tooltip tooltip={deleteTooltip}><Button icon="delete" onPress={onDelete} { ...iconProps } { ...buttonGroupProps } /></Tooltip>
                                <Tooltip tooltip={shareTooltip}><Button onPress={shareView} icon="share" variant={isShared ? "ghost_faded" : undefined} { ...iconProps }  { ...(isShared ? {} : buttonGroupProps) } /></Tooltip>
                            </Button.ButtonGroup>
                        )
                }
                {
                    isOwnView
                        ? null
                        : <Tooltip tooltip={shareTooltip}><Button flexShrink="0" onPress={handleChangeActiveView} icon="lock" _hover={hoveredState} { ...iconProps } /></Tooltip>
                }
            </HStack>
        </View>
    )
}

const FilteredViewRowRX = connect(
    mapStateToProps,
    mapDispatchToProps,
)(FilteredViewRow)

export {
    FilteredViewRowRX as FilteredViewRow
}